/**
 * Mecom Design System — formattazione dei dati.
 * Solo presentazione: nessun calcolo di business. Nessuna dipendenza.
 * Convenzioni allineate a src/components/laser/kpi (43,3%, pp, —, ▲ +, ▼ −).
 */
const LOCALE = "it-IT";
export function clock(value) {
    const stamp = value ? new Date(value) : null;
    return stamp && Number.isFinite(stamp.getTime()) ? stamp.toLocaleTimeString(LOCALE, { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" }) : "—";
}
export function dayLabel(value) {
    const stamp = new Date(value + 'T12:00:00Z');
    if (!Number.isFinite(stamp.getTime())) return '—';
    return stamp.toLocaleDateString(LOCALE, {timeZone:'Europe/Rome',weekday:'short',day:'2-digit',month:'2-digit'}).replace(',', '');
}
/** Wall-clock input in Rome, independent of the workstation timezone. */
export function romeDateTimeInput(value = new Date()) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('sv-SE', { timeZone:'Europe/Rome', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', hourCycle:'h23' }).formatToParts(value).map(p=>[p.type,p.value]));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
export function romeInputToIso(value) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value || '')) return null;
    const matches = [60,120].map(minutes=>new Date(Date.parse(value+'Z')-minutes*60000)).filter(d=>Number.isFinite(d.getTime())&&romeDateTimeInput(d)===value);
    return matches.length===1 ? matches[0].toISOString() : null;
}
export const MISSING = "—";
const MINUS = "−"; // U+2212

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const fmt = (v, min = 0, max = 1, useGrouping = "always") =>
    new Intl.NumberFormat(LOCALE, { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping }).format(v);

/** 1234.56 -> "1.234,6"; minimumDecimals conserva gli zeri finali se richiesto. */
export function number(value, decimals = 1, minimumDecimals = 0, useGrouping = "always") {
    return isNum(value) ? fmt(value, minimumDecimals, decimals, useGrouping) : MISSING;
}

/** Valore con unità: (142,"h") -> "142 h"; (43.3,"%") -> "43,3%"; null -> "—". */
export function withUnit(value, unit = "", decimals = 1) {
    if (!isNum(value)) return MISSING;
    if (unit === "%") return fmt(value, Number.isInteger(value) ? 0 : Math.min(decimals, 1), decimals) + "%";
    return unit ? `${number(value, decimals)} ${unit}` : number(value, decimals);
}

/** Percentuale da quota 0–1: 0.433 -> "43,3%". */
export function percentOfShare(share, decimals = 1) {
    return isNum(share) ? withUnit(share * 100, "%", decimals) : MISSING;
}

/** Variazione con segno tipografico: 5 -> "+5 h", -3.1 -> "−3,1 h", 0 -> "0 h". */
export function signed(value, unit = "", decimals = 1) {
    if (!isNum(value)) return MISSING;
    const abs = withUnit(Math.abs(value), unit === "pp" ? "" : unit, decimals) + (unit === "pp" ? " pp" : "");
    if (value > 0) return "+" + abs;
    if (value < 0) return MINUS + abs;
    return abs;
}

/**
 * Giudizio di una variazione.
 * @param {number} delta
 * @param {boolean} higherIsBetter  verso buono dell'indicatore
 * @param {number} noise            sotto questa soglia (assoluta) è "neutral"
 * @returns {"good"|"bad"|"neutral"}
 */
export function deltaTone(delta, higherIsBetter = true, noise = 0) {
    if (!isNum(delta) || Math.abs(delta) <= noise) return "neutral";
    return (delta > 0) === higherIsBetter ? "good" : "bad";
}

/** Testo del Delta con freccia: "▲ +5 h", "▼ −3,1 pp", "= 0,2 h". */
export function deltaLabel(delta, unit = "", { noise = 0, decimals = 1 } = {}) {
    if (!isNum(delta)) return MISSING;
    const arrow = Math.abs(delta) <= noise ? "=" : delta > 0 ? "▲" : "▼";
    const text = arrow === "=" ? withUnit(Math.abs(delta), unit === "pp" ? "" : unit, decimals) + (unit === "pp" ? " pp" : "") : signed(delta, unit, decimals);
    return `${arrow} ${text}`;
}

/** Classe CSS del Delta: "mc-delta mc-delta--good". */
export function deltaClass(delta, higherIsBetter = true, noise = 0) {
    return `mc-delta mc-delta--${deltaTone(delta, higherIsBetter, noise)}`;
}

/** Durata in minuti: 13 -> "13 min", 95 -> "1 h 35 min", 0.5 -> "30 s". */
export function duration(minutes) {
    if (!isNum(minutes)) return MISSING;
    if (minutes < 1) return `${Math.round(minutes * 60)} s`;
    if (minutes < 60) return `${number(minutes, 0)} min`;
    const h = Math.floor(minutes / 60), m = Math.round(minutes % 60);
    return m ? `${h} h ${m} min` : `${h} h`;
}

const toDate = (v) => (v instanceof Date ? v : typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v + "T12:00:00Z") : v ? new Date(v) : null);
const valid = (d) => d instanceof Date && !Number.isNaN(d.getTime());

/** "06/10/2026"; date segnaposto (anno 1999 / 9999) -> "—". */
export function date(value) {
    const d = toDate(value);
    if (!valid(d) || d.getFullYear() <= 1999 || d.getFullYear() >= 9999) return MISSING;
    return d.toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" });
}

/** "06/10, 07:20" per la freschezza dei dati. */
export function updatedAt(value) {
    const d = toDate(value);
    if (!valid(d)) return MISSING;
    const day = d.toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit", timeZone: "Europe/Rome" });
    const time = d.toLocaleTimeString(LOCALE, { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
    return `${day}, ${time}`;
}

/** Settimana ISO: "S40". */
export function isoWeek(value) {
    const d0 = toDate(value);
    if (!valid(d0)) return MISSING;
    const calendar = new Intl.DateTimeFormat('sv-SE',{timeZone:'Europe/Rome'}).format(d0);
    const d = new Date(calendar+'T12:00:00Z');
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return "S" + Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/** "ott 2026". */
export function monthLabel(value) {
    const d = toDate(value);
    return valid(d) ? d.toLocaleDateString(LOCALE, { timeZone:'Europe/Rome',month: "short", year: "numeric" }).replace(".", "") : MISSING;
}

/** Quantità avanzate: (0, 72) -> "0 / 72 pz". */
export function progressQty(done, total, unit = "pz") {
    if (!isNum(done) || !isNum(total)) return MISSING;
    return `${number(done, 0)} / ${number(total, 0)} ${unit}`;
}
